/* GLOP motion + small global glue.
   1. Scroll reveals: elements rise 16px and fade in over 400ms as they enter the viewport.
      Driven by scroll/resize on the real scroll container (Horizon's .page-wrapper on
      desktop, window on mobile) — not IntersectionObserver, which does not fire reliably
      inside that container. Fail-safe: everything is revealed after 4s regardless.
   2. Hero parallax: the campaign photograph drifts at ~0.85x scroll speed.
   3. Header cart count: re-read /cart.js whenever Horizon re-renders the cart drawer.
   Motion is disabled under prefers-reduced-motion; the cart count glue always runs. */
(function () {
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function scroller() {
    var pw = document.querySelector('.page-wrapper');
    if (pw && getComputedStyle(pw).overflowY === 'auto') return pw;
    return null; // window scrolls
  }

  function onScroll(fn) {
    var src = scroller();
    var ticking = false;
    function handler() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        fn();
      });
    }
    (src || window).addEventListener('scroll', handler, { passive: true });
    window.addEventListener('resize', handler, { passive: true });
    window.addEventListener('load', handler);
    return handler;
  }

  /* ---------------- Reveals ---------------- */
  var REVEAL = [
    '.glop-head',
    '.glop-hero__foot > *',
    '.glop-shades__copy > *',
    '.glop-card',
    '.glop-looks__figure',
    '.glop-collection__header > *',
    '.glop-editorial-break > *',
    '.glop-product__copy > *',
    '.glop-htu__panel',
    '.glop-about-hero__caption',
    '.glop-manifesto > *',
    '.glop-pillar',
    '.glop-palette-origins > *',
    '.glop-closing-cta > *',
    '.glop-article__header > *',
    '.glop-article__body > *',
    '.glop-faq__row',
    '.glop-contact__card',
    '.glop-footer__top > *',
    '.glop-footer__col'
  ].join(',');

  var pending = [];

  function tagReveals(root) {
    var nodes = root.querySelectorAll(REVEAL);
    var lastParent = null;
    var i = 0;
    nodes.forEach(function (el) {
      if (el.hasAttribute('data-glop-reveal')) return;
      if (el.parentElement !== lastParent) {
        lastParent = el.parentElement;
        i = 0;
      }
      el.setAttribute('data-glop-reveal', '');
      el.style.setProperty('--glop-reveal-i', String(Math.min(i, 7)));
      pending.push(el);
      i++;
    });
  }

  function checkReveals() {
    if (!pending.length) return;
    var vh = window.innerHeight;
    var keep = [];
    for (var k = 0; k < pending.length; k++) {
      var el = pending[k];
      var r = el.getBoundingClientRect();
      if (r.top < vh * 0.94 && r.bottom > 0) {
        el.classList.add('is-in');
      } else {
        keep.push(el);
      }
    }
    pending = keep;
  }

  function revealAll() {
    document.querySelectorAll('[data-glop-reveal]:not(.is-in)').forEach(function (el) {
      el.classList.add('is-in');
    });
    pending = [];
  }

  /* ---------------- Hero parallax ---------------- */
  function parallaxFactory() {
    var media = document.querySelector('.glop-hero__media');
    if (!media) return function () {};
    var panel = media.closest('.glop-hero__panel');
    return function () {
      var rect = panel.getBoundingClientRect();
      var progress = -rect.top;
      if (progress < 0) progress = 0;
      if (progress > rect.height) progress = rect.height;
      media.style.setProperty('--glop-parallax', (progress * 0.15).toFixed(1) + 'px');
    };
  }

  /* ---------------- Header cart count ---------------- */
  function cartCount() {
    var targets = document.querySelectorAll('[data-glop-cart-count]');
    if (!targets.length) return;
    var timer = null;
    function refresh() {
      clearTimeout(timer);
      timer = setTimeout(function () {
        fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            targets.forEach(function (el) {
              el.textContent = String(cart.item_count);
              el.setAttribute('data-count', String(cart.item_count));
            });
          })
          .catch(function () {});
      }, 250);
    }
    var drawer = document.querySelector('#shopify-section-cart-drawer-section') || document.querySelector('#cart-drawer, cart-drawer-component');
    if (drawer && 'MutationObserver' in window) {
      new MutationObserver(refresh).observe(drawer, { childList: true, subtree: true });
    }
    ['cart:update', 'cart:lines-update', 'cart:change', 'cart:added'].forEach(function (name) {
      document.addEventListener(name, refresh);
    });
    window.addEventListener('pageshow', refresh);
  }

  function boot() {
    cartCount();
    if (reduce) return;
    tagReveals(document);
    var parallax = parallaxFactory();
    var tick = onScroll(function () {
      checkReveals();
      parallax();
    });
    tick();
    setTimeout(tick, 300);
    setTimeout(revealAll, 4000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (reduce) return;
    tagReveals(e.target);
    checkReveals();
  });
})();

/* Background videos on iPhone. Safari refuses autoplay in Low Power Mode (and sometimes
   before the page is interacted with) and then paints its big play button over the
   clip. The button is hidden in glop.css, so the poster/first frame shows instead, and
   the clips start on the visitor's first touch or scroll, which Safari counts as
   permission. Only muted, control-less autoplay videos are touched. */
(function () {
  function stalled() {
    return Array.prototype.filter.call(document.querySelectorAll('video[autoplay]:not([controls])'), function (v) {
      return v.paused && v.muted !== false;
    });
  }
  function kick() {
    stalled().forEach(function (v) {
      v.muted = true;
      v.playsInline = true;
      var p = v.play();
      if (p && p.catch) p.catch(function () {});
    });
  }
  function once() {
    kick();
    if (!stalled().length) {
      ['touchstart', 'pointerdown', 'scroll', 'keydown'].forEach(function (t) {
        window.removeEventListener(t, once, true);
      });
    }
  }
  ['touchstart', 'pointerdown', 'scroll', 'keydown'].forEach(function (t) {
    window.addEventListener(t, once, { capture: true, passive: true });
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', kick);
  else kick();
  // coming back to the tab (Safari pauses background clips)
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) kick();
  });
})();
